import {
  Button,
  Containers,
  FeaturedIcon,
  Flex,
  Icon,
  ProgressBar,
  Typography,
} from '@jisr-hr/ds-beta'
import {
  getSurveyQuestionsQueryKey,
  useGetSurveyQuestions,
} from 'apis/Surveys/useGetSurveyQuestions'
import { useState } from 'react'
import { isRtl } from 'utils/uiHelpers'
import JisrLoader from 'components/global/JisrLoader'
import i18n from 'translations/i18n'
import { useHistory } from 'react-router-dom'
import { QuestionTypeEnum } from 'apis/Surveys/enum'
import { useFillSurveyForm } from 'apis/Surveys/useFillSurveyForm'
import { queryClient } from 'hooks/useReactQuery'
import { ReactComponent as JisrLogo } from 'assets/images/JisrLogo.svg'
import { SurveyFillBody } from 'apis/Surveys/types'
import { mixPanelAnalytics } from 'utils/mixpanel'
import { SingleChoiceAnswers } from './components/SingleChoiceAnswers'
import { MultiChoiceAnswers } from './components/MultiChoiceAnswers'
import { EmojiChoiceAnswers } from './components/EmojiChoiceAnswer'
import { NpsAnswers } from './components/NpsAnswer'
import { TextBasedAnswers } from './components/TextBasedAnswer'
import { SucessImageIllustation } from '../constants'

export default function SurveyForm() {
  const history = useHistory()
  const surveyIdMatch = window.location.href.match(/\/survey\/(\d+)/)
  const surveyId = surveyIdMatch ? surveyIdMatch[1] : null

  const [answers, setAnswers] = useState<Record<string, string | string[] | number>>({})

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0)
  const { mutate: updateSurveyAnswers, isLoading: isLoadingUpdate } = useFillSurveyForm()

  const { data, isLoading } = useGetSurveyQuestions(`${surveyId}`, {
    query: {
      enabled: !!surveyId,
    },
  })

  const surveyQuestions = data?.survey_questions || []

  const activeQuestionNumber = activeQuestionIndex + 1
  const activeQuestion = surveyQuestions?.[activeQuestionIndex]
  const activeQuestionAnswer = activeQuestion?.id ? answers[activeQuestion?.id] : undefined
  const handleChange = (value: string | string[] | number) => {
    if (activeQuestion) {
      setAnswers((prev) => ({
        ...prev,
        [activeQuestion.id]: value,
      }))
    }
  }

  const progress = activeQuestionNumber / (surveyQuestions.length || 1)
  const progressPercentage = Math.round(progress * 100)

  const isNextDisabled =
    activeQuestionNumber > surveyQuestions?.length ||
    (activeQuestion?.metadata.is_required &&
      (activeQuestionAnswer === undefined || activeQuestionAnswer === ''))
  const isPreviousDisabled = activeQuestionNumber <= 1
  const isLastQuestion = activeQuestionNumber === surveyQuestions?.length
  const handleNext = async () => {
    if (isLastQuestion && surveyId) {
      const apiPayload: SurveyFillBody = Object.entries(answers).map(([questionId, value]) => ({
        survey_question_id: Number(questionId),
        value,
        survey_id: Number(surveyId),
      }))

      const filterPayload = apiPayload.filter(
        (item) => item.value !== undefined || item.value !== null || item.value !== '',
      )

      await updateSurveyAnswers(
        { data: filterPayload },
        {
          onSuccess: () => {
            mixPanelAnalytics({
              event_name: 'Participant Completed',
              properties: {
                survey_id: surveyId,
                platform: 'web',
                question_count: surveyQuestions.length,
                questions_answered_count: Object.keys(answers).length,
                flow_type: 'notification',
              },
            })
            queryClient.invalidateQueries([getSurveyQuestionsQueryKey(surveyId)[0]])
          },
        },
      )
    } else {
      setActiveQuestionIndex((prev) => prev + 1)
    }
  }

  if (data?.is_submitted) {
    return (
      <>
        <div
          className={`p-4 cursor-pointer absolute top-0 ${isRtl ? 'left-5' : 'right-5'}`}
          onClick={() => {
            mixPanelAnalytics({
              event_name: 'Participant Abandoned',
              properties: {
                survey_id: surveyId,
                flow_type: 'notification',
                question_count: surveyQuestions.length,
                abandoned_question_number: activeQuestionNumber,
                platform: 'web',
              },
            })
            history.push('/')
          }}
        >
          <FeaturedIcon
            iconName="x-close"
            colorType="neutral"
            size="lg"
          />
        </div>

        <div className="flex flex-col gap-4 justify-center align-items-center w-[60vw] mobile:w-[90vw] mx-auto h-[90vh]">
          <img
            src={SucessImageIllustation}
            alt="success"
            height={200}
            width={200}
          />
          <br />
          <Typography
            text={i18n.t('label.end_of_the_survey')}
            textColor="color/fg/default"
            variant="heading"
          />
          <Typography
            text={i18n.t('label.you_may_safely_close_this_window')}
            variant="interface/medium/m"
            textColor="color/fg/lighter"
          />
          <Button
            size="medium"
            type="button"
            label={i18n.t('label.close')}
            onClick={() => history.push('/')}
            className="w-40 mt-4"
          />
        </div>
      </>
    )
  } else if (data?.is_closed) {
    return (
      <>
        <div
          className={`p-4 cursor-pointer absolute top-0 ${isRtl ? 'left-5' : 'right-5'}`}
          onClick={() => {
            history.push('/')
            mixPanelAnalytics({
              event_name: 'Participant Abandoned',
              properties: {
                survey_id: surveyId,
                flow_type: 'notification',
                question_count: surveyQuestions.length,
                abandoned_question_number: activeQuestionNumber,
                platform: 'web',
              },
            })
          }}
        >
          <FeaturedIcon
            iconName="x-close"
            colorType="neutral"
            size="lg"
          />
        </div>

        <div className="flex flex-col gap-4 justify-center align-items-center w-[60vw] mobile:w-[90vw] mx-auto h-[90vh]">
          <Containers
            borderRadius="round"
            className="bg-color-bg-warning-lightest-default !h-[206px] !w-[206px] flex justify-center items-center"
          >
            <Icon
              name="alert-triangle-filled"
              className="h-[100px] w-[100px] text-color-fg-warning-default"
            />
          </Containers>
          <br />
          <Typography
            text={i18n.t('label.survey_closed')}
            textColor="color/fg/default"
            variant="heading"
          />
          <Typography
            text={i18n.t('label.survey_closed_by_the_admin')}
            variant="interface/medium/m"
            textColor="color/fg/lighter"
          />
          <Button
            size="medium"
            type="button"
            label={i18n.t('label.close')}
            onClick={() => history.push('/')}
            className="w-40 mt-4"
          />
        </div>
      </>
    )
  }

  if (isLoading) {
    return <JisrLoader />
  }

  return (
    <div>
      <div
        className={`p-4 cursor-pointer absolute top-0 ${isRtl ? 'left-5' : 'right-5'}`}
        onClick={() => history.push('/')}
      >
        <FeaturedIcon
          iconName="x-close"
          colorType="neutral"
          size="lg"
        />
      </div>
      <div className="flex flex-col items-center gap-4 justify-center w-[60vw] mobile:w-[90vw] mx-auto h-[90vh]">
        <JisrLogo
          width="50px"
          height="30px"
        />
        <div className="w-full mt-10">
          <ProgressBar
            value={progress}
            trackColor="gray"
            content={`${progressPercentage}%`}
            direction="horizontal"
          />
        </div>
        <Typography
          text={`(${activeQuestionNumber})`}
          variant="heading"
        />
        <Flex dir={isRtl ? 'row-reverse' : 'row'}>
          <Typography
            variant="heading"
            className="survey-question-number"
            text={activeQuestion?.title_i18n}
          />
          {activeQuestion?.metadata.is_required ? (
            <Typography
              variant="heading"
              className="survey-question-number"
              textColor="color/fg/danger/default"
              text="*"
            />
          ) : null}
        </Flex>

        {activeQuestion?.question_type === QuestionTypeEnum.SINGLE_CHOICE ? (
          <>
            <SingleChoiceAnswers
              value={activeQuestionAnswer as string}
              handleChange={handleChange}
              options={activeQuestion?.metadata?.options?.map((option) => ({
                label: `${option.value_i18n}`,
                value: `${option.value_i18n}`,
              }))}
            />
          </>
        ) : null}
        {activeQuestion?.question_type === QuestionTypeEnum.MULTIPLE_CHOICE ? (
          <MultiChoiceAnswers
            value={activeQuestionAnswer as string[]}
            handleChange={handleChange}
            options={activeQuestion?.metadata?.options?.map((option) => ({
              label: `${option.value_i18n}`,
              value: `${option.value_i18n}`,
            }))}
          />
        ) : null}
        {activeQuestion?.question_type === QuestionTypeEnum.NPS ? (
          <NpsAnswers
            handleChange={handleChange}
            value={activeQuestionAnswer as string}
          />
        ) : null}
        {activeQuestion?.question_type === QuestionTypeEnum.RATING ? (
          <EmojiChoiceAnswers
            handleChange={handleChange}
            value={activeQuestionAnswer as string}
          />
        ) : null}
        {activeQuestion?.question_type === QuestionTypeEnum.OPEN_TEXT ? (
          <>
            <TextBasedAnswers
              value={(activeQuestionAnswer as string) || ''}
              handleChange={handleChange}
            />
          </>
        ) : null}

        <div className="flex flex-row gap-2 self-end mt-5">
          <Button
            size="medium"
            variant="outlined"
            label={i18n.t('cta.previous')}
            testId="download"
            onClick={() => setActiveQuestionIndex((prev) => prev - 1)}
            disabled={isPreviousDisabled}
          />

          <Button
            size="medium"
            type="submit"
            label={isLastQuestion ? i18n.t('label.done') : i18n.t('label.next_question')}
            onClick={handleNext}
            disabled={isNextDisabled || isLoadingUpdate}
            trailingIcon={isRtl ? 'arrow-left' : 'arrow-right'}
          />
        </div>
      </div>
    </div>
  )
}
